/**
 * A box for looking something up, where the looking is the server's job.
 *
 * Everywhere this is used -- a view's rows, an instance's accounts, its
 * workspaces -- the search narrows what comes back rather than highlighting
 * matches in what is already on screen. That is not a shortcut: each of them
 * holds a page or a window out of however many there are, so a match outside it
 * is a highlight nobody can see.
 *
 * The term belongs in the address, so the caller writes it there and hands it
 * back through `term`. Everything subtle here is about the gap between the two:
 * the debounce means the address lags what has been typed by up to a moment, and
 * a term that arrives from anywhere else -- the back button, a link somebody
 * sent, a different view -- has to win over what is half-typed without the two
 * fighting.
 */
import { useEffect, useRef, useState } from 'react'
import { Search, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Input } from '@/shared/ui/input'

/**
 * How long the box waits before asking.
 *
 * Long enough that typing a word is one request rather than one per letter, and
 * short enough that stopping feels like an answer. Each keystroke also writes
 * the address, so this is a history entry per pause as well as a request.
 */
export const SEARCH_DELAY = 300

export function SearchBox({
  term,
  onSearch,
  label,
  className = 'h-8 w-40 pr-7 pl-7 lg:w-56',
}: {
  /** What the address says is being searched for. */
  term: string
  onSearch: (term: string) => void
  /** What is being searched, since a page may hold more than one of these. */
  label: string
  className?: string
}) {
  const { t } = useTranslation()
  const [text, setText] = useState(term)
  const pending = useRef<ReturnType<typeof setTimeout>>(undefined)

  function cancel(): void {
    clearTimeout(pending.current)
  }

  // The debounce hangs off typing rather than off the state, which is the
  // difference between "wait before asking for what was typed" and "wait
  // whenever this value differs from the address". The second needs a guard
  // against asking for what the address already says, and that guard is a
  // branch no behaviour depends on.
  function type(next: string): void {
    setText(next)
    cancel()
    pending.current = setTimeout(() => {
      asked.current = next
      onSearch(next)
    }, SEARCH_DELAY)
  }

  useEffect(() => cancel, [])

  // What this box last asked for, so a term that arrives from anywhere else --
  // the back button, a link somebody sent, switching views -- is recognised as
  // somebody else's and adopted. Adopting the term unconditionally would fight
  // the debounce, because for a moment after every keystroke the address still
  // says what it said before.
  const asked = useRef(term)
  if (term !== asked.current) {
    asked.current = term
    setText(term)
    // Whatever was half-typed is abandoned with it. Letting it fire would
    // overwrite the term that has just arrived with one from before it.
    cancel()
  }

  /** Clears without waiting: a clear is a decision, not a keystroke. */
  function clear(): void {
    cancel()
    asked.current = ''
    setText('')
    onSearch('')
  }

  return (
    <div className="relative">
      <Search
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-muted-foreground"
      />
      <Input
        type="search"
        value={text}
        aria-label={label}
        placeholder={label}
        onChange={(event) => {
          type(event.target.value)
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Escape') return
          // Stopped here so that Escape in the box means "clear the search"
          // rather than reaching whatever else is listening for it.
          event.preventDefault()
          event.stopPropagation()
          clear()
        }}
        className={className}
      />
      {text !== '' && (
        <button
          type="button"
          aria-label={t('view.searchClear')}
          onClick={clear}
          className="absolute top-1/2 right-1 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-muted"
        >
          <X className="size-3" />
        </button>
      )}
    </div>
  )
}
